import React, { createRef, useState } from 'react';
import Modal from '../Modal/Modal';
import styles from './InstagramShareModal.scss';
import JgButtonGroup from '@justgiving/button-group';
import { InstagramIcon } from '../SocialIcons/InstagramIcon';
import { useApp, useBasePageQuery } from '../../hooks';
import { filterByTypename } from '../../utils/graphql';
import { useScreenshot, dataURItoBlob } from '../../hooks/useScreenshot';
import {
  IG_SHARE_TYPE,
  InstagramShare,
} from '../InstagramShare/InstagramShare';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';
import { InstagramShareScreenshot } from '../InstagramShare/InstagramShareScreenshot';

export interface InstagramShareModalProps {
  onClose: () => void;
}

export const InstagramShareModal: React.FunctionComponent<InstagramShareModalProps> = ({
  onClose,
}) => {
  const { t } = useTranslation('common', { useSuspense: false });
  const ref = createRef<HTMLDivElement>();
  const { takeInstagramScreenshot } = useScreenshot({
    type: 'image/jpeg',
    quality: 2.0,
    canvasOptions: {
      useCORS: true,
      scale: 2,
      onclone: clonedDoc => {
        const container = clonedDoc.getElementById('container');
        const logo = clonedDoc.getElementById('logo');

        if (container) {
          container.style.overflow = 'visible';
        }
        if (logo) {
          logo.style.overflow = 'visible';
        }
      },
    },
  });

  const [selectedOption, setSelectedOption] = useState<string>(
    'Instagram Post',
  );

  const { showToast } = useApp();
  const { data: basePageData } = useBasePageQuery();

  if (!basePageData?.page) {
    return null;
  }

  const { hero, cover } = basePageData.page;

  let heroImageUrl = (hero?.imageUrl ||
    (cover?.__typename === 'ImageMedia' && cover?.url)) as string;
  let altText =
    hero?.imageTitle || (cover?.__typename === 'ImageMedia' && cover.alt) || '';

  const charity = basePageData?.page?.relationships?.beneficiaries?.nodes
    ? filterByTypename(
        basePageData?.page?.relationships?.beneficiaries?.nodes,
        'Charity',
      )
    : null;
  const charityName = charity?.[0]?.name ?? 'charity';
  const pageUrl = window.location.href;

  const shareImage = async () => {
    const image = await takeInstagramScreenshot(ref.current!);
    if (navigator.share && image) {
      try {
        await navigator.share({
          files: [
            new File([dataURItoBlob(image)], 'instagramShare.jpg', {
              type: 'image/jpeg',
            }),
          ],
        } as ShareData);
      } catch (error) {
        showToast({
          message: t('shareModal.instagram.errorWhenSharing'),
          type: 'warning',
          dismissAfter: 3000,
        });
      }
    } else {
      showToast({
        message: t('shareModal.instagram.unsupportedBrowser'),
        type: 'warning',
        dismissAfter: 3000,
      });
    }
  };

  return (
    <Modal
      className={styles.modal}
      displayCloseButton
      onCloseRequest={onClose}
      scrollLock={false}
    >
      <div className={`row ${styles.topContainer}`}>
        <p className={styles.modalTitle}>
          {t('shareModal.instagram.chooseATemplate')}
        </p>
      </div>
      <JgButtonGroup
        onOptionClick={e => setSelectedOption(e.label)}
        options={[
          { label: 'Instagram Post', value: 'Instagram Post' },
          { label: 'Instagram Story', value: 'Instagram Story' },
        ]}
        selectedOption={selectedOption}
      />
      <InstagramShare
        heroImageUrl={heroImageUrl}
        altText={altText}
        charityName={charityName}
        pageUrl={pageUrl}
        type={
          selectedOption === 'Instagram Post'
            ? IG_SHARE_TYPE.POST
            : IG_SHARE_TYPE.STORY
        }
      />
      <InstagramShareScreenshot
        heroImageUrl={heroImageUrl}
        altText={altText}
        screenshotRef={ref}
        type={
          selectedOption === 'Instagram Post'
            ? IG_SHARE_TYPE.POST
            : IG_SHARE_TYPE.STORY
        }
      />
      <Button className={styles.shareButton} onClick={() => shareImage()}>
        {<InstagramIcon width="24px" height="24px" color="#fff" />}{' '}
        {t('shareModal.instagram.share')}{' '}
      </Button>
    </Modal>
  );
};
